import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  CognitoUserSession,
  type ISignUpResult,
} from "amazon-cognito-identity-js";

// Pool config comes from build-time env vars, and RW-27 is the reminder that
// TWO paths supply them, both real:
//   - `infra/web-rnw.ts:50-54` — SST hands them to the StaticSite on deploy.
//     (The old note here pointed at `infra/web.ts`, which is the LEGACY web
//     app's file, not this one.)
//   - `scripts/publish-web.sh:104-111` — the manual publish reads the stage's
//     coordinates from `infra/stages.json` and passes the same five on the
//     build command line, deliberately with no defaults.
// ⛔ What does NOT supply them is a `.env.production`. There is none, and there
// cannot be one: `.gitignore` covers `.env.*`. Anyone reaching for that file to
// "fix" a blank local build is about to commit stage config into the repo.
//
// ⚠️ "Fail loud" holds only with devtools open: this throws at MODULE LOAD,
// before React mounts, so a build made without the vars is a blank root and a
// console line. Consequences and the way round it: serve-local.sh, trap 2.
const userPoolId = import.meta.env["VITE_USER_POOL_ID"];
const clientId = import.meta.env["VITE_USER_POOL_CLIENT_ID"];
if (!userPoolId || !clientId) {
  throw new Error(
    "Cognito env not configured (VITE_USER_POOL_ID / VITE_USER_POOL_CLIENT_ID).",
  );
}

// Token storage:
//   amazon-cognito-identity-js defaults to window.localStorage. That's the
//   standard SPA trade — XSS-readable, but cookies (httpOnly + CSRF) require
//   a backend session layer we don't have and don't need at family scale.
//   Mitigation: strict CSP at the CloudFront layer once we add it, and we
//   rotate refresh tokens (enableTokenRevocation in infra/auth.ts).
//   If we ever want to swap to in-memory + httpOnly cookies, this is the
//   single seam to change (pass `Storage` here).
const pool = new CognitoUserPool({
  UserPoolId: userPoolId,
  ClientId: clientId,
});

const userFor = (email: string) =>
  new CognitoUser({ Username: email, Pool: pool });

export type AuthError = { code: string; message: string };

const toAuthError = (err: unknown): AuthError => {
  if (err && typeof err === "object") {
    const e = err as { code?: string; name?: string; message?: string };
    return {
      code: e.code ?? e.name ?? "UnknownError",
      message: e.message ?? "Unknown error",
    };
  }
  return { code: "UnknownError", message: String(err) };
};

export const signUp = (email: string, password: string) =>
  new Promise<ISignUpResult>((resolve, reject) => {
    pool.signUp(
      email,
      password,
      [new CognitoUserAttribute({ Name: "email", Value: email })],
      [],
      (err, result) => {
        if (err || !result) return reject(toAuthError(err));
        resolve(result);
      },
    );
  });

export const confirmSignUp = (email: string, code: string) =>
  new Promise<void>((resolve, reject) => {
    userFor(email).confirmRegistration(code, true, (err) => {
      if (err) return reject(toAuthError(err));
      resolve();
    });
  });

export const resendConfirmationCode = (email: string) =>
  new Promise<void>((resolve, reject) => {
    userFor(email).resendConfirmationCode((err) => {
      if (err) return reject(toAuthError(err));
      resolve();
    });
  });

// Triggers Cognito's password-reset flow: a code is sent to the email
// on file. The promise resolves once the code is in flight; the user
// then types the code + a new password into confirmForgotPassword().
// preventUserExistenceErrors is enabled on the pool, so an unknown
// email also resolves successfully — the surface stays opaque.
export const forgotPassword = (email: string) =>
  new Promise<void>((resolve, reject) => {
    userFor(email).forgotPassword({
      onSuccess: () => resolve(),
      inputVerificationCode: () => resolve(),
      onFailure: (err) => reject(toAuthError(err)),
    });
  });

export const confirmForgotPassword = (
  email: string,
  code: string,
  newPassword: string,
) =>
  new Promise<void>((resolve, reject) => {
    userFor(email).confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(toAuthError(err)),
    });
  });

export const signIn = (email: string, password: string) =>
  new Promise<CognitoUserSession>((resolve, reject) => {
    const user = userFor(email);
    user.authenticateUser(
      new AuthenticationDetails({ Username: email, Password: password }),
      {
        onSuccess: (session) => resolve(session),
        onFailure: (err) => reject(toAuthError(err)),
      },
    );
  });

export const signOut = () => {
  const u = pool.getCurrentUser();
  if (u) u.signOut();
};

// Change the signed-in user's password (Settings → Account). No email
// round-trip: the current session authorises the change. Mirrors the
// mobile cognito.changePassword.
export const changePassword = (oldPassword: string, newPassword: string) =>
  new Promise<void>((resolve, reject) => {
    const u = pool.getCurrentUser();
    if (!u) return reject(toAuthError({ code: "NoCurrentUser" }));
    u.getSession((err: Error | null) => {
      if (err) return reject(toAuthError(err));
      u.changePassword(oldPassword, newPassword, (e) => {
        if (e) return reject(toAuthError(e));
        resolve();
      });
    });
  });

// Resolves to a fresh, valid session — the SDK refreshes the access/id
// tokens automatically when called via getSession() if the refresh token
// is still valid. Returns null if nobody is signed in or the refresh
// token has expired.
export const getCurrentSession = () =>
  new Promise<CognitoUserSession | null>((resolve) => {
    const u = pool.getCurrentUser();
    if (!u) return resolve(null);
    u.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) return resolve(null);
      resolve(session);
    });
  });

// ID token is what we send to API Gateway (its audience matches the
// client ID, which is the audience configured on the JWT authorizer).
export const getIdToken = async (): Promise<string | null> => {
  const session = await getCurrentSession();
  return session ? session.getIdToken().getJwtToken() : null;
};
